import type { ReactNode } from 'react';
import coffmanLogo from '../../assets/coffman-logo.png';
import loginSite from '../../assets/login-site.jpg';

/**
 * THE PHOTO SWAP LIVES HERE, AND ONLY HERE.
 *
 * Set to `null` to fall back to a flat `--ink` panel with a 4% hairline grid —
 * that path is kept live because it is what shipped before the client supplied
 * a photo, and it is the honest failure mode if the asset is ever pulled.
 *
 * IMPORT the file, never a raw path: the import gets content-hashed by Vite,
 * and the production CSP is `img-src 'self' data: blob:` — a remote URL would
 * be blocked at the browser and the panel would silently go flat in prod only.
 *
 * The crop anchor is in `.auth-panel-photo` (`object-position`), not here. It
 * is load-bearing — see the note there.
 */
const AUTH_PANEL_IMAGE: string | null = loginSite;

interface AuthLayoutProps {
  /** The H1. Every auth screen has exactly one. */
  title: string;
  /** The line under it. 14px, --ink-3. */
  subtitle: string;
  children: ReactNode;
}

/**
 * The shell all four auth screens sit in — login, forgot password, reset
 * password, accept invite.
 *
 * It owns the split panel AND the head (logo → title → subtitle), because the
 * thing that makes four separate pages read as one system is that the logo does
 * not move by two pixels between them. Pages supply only their body.
 *
 * Layout: 50/50 split at ≥1024px, single 420px column at 640–1023px, 24px
 * padding at <640px. The photo panel is HIDDEN below 1024px rather than stacked
 * above the form — see the note in the CSS block.
 */
export function AuthLayout({ title, subtitle, children }: AuthLayoutProps) {
  const photo = AUTH_PANEL_IMAGE;

  return (
    <main className="auth-shell">
      <div className="auth-form-col">
        <div className="auth-form">
          <img className="auth-logo" src={coffmanLogo} alt="Coffman Excavation" />
          <div className="auth-head">
            <h1 className="auth-h1">{title}</h1>
            <p className="auth-sub">{subtitle}</p>
          </div>
          <div className="auth-body">{children}</div>
        </div>
      </div>

      {/*
        Entirely decorative for assistive tech: the wordmark repeats the logo
        the form column already labels, and the caption repeats the subtitle.
        Announcing both twice on a login screen is noise, not context.
      */}
      <aside className="auth-panel" aria-hidden="true">
        {photo ? (
          <img className="auth-panel-photo" src={photo} alt="" decoding="async" />
        ) : (
          <div className="auth-panel-grid" />
        )}
        <div className="auth-panel-scrim" />
        <div className="auth-panel-inner">
          {/*
            The wordmark is rendered from the logo asset, never composited into
            the photograph — a baked-in logo resamples with the photo and goes
            soft, and it can't respond to the scrim. This one stays vector-sharp
            at every panel size.
          */}
          <img className="auth-panel-mark" src={coffmanLogo} alt="" />
          {/*
            NO JOB NAME, NO CITY. The client caught us naming the wrong city on
            this project once already — it is not Hillsboro — and a sign-in
            screen is the worst place to repeat a mistake, because he would read
            it every morning. The portal is also no longer single-project, so
            naming any one job here would be wrong even if the city were right.
          */}
          <p className="auth-panel-caption">Time &amp; Materials Portal</p>
        </div>
      </aside>
    </main>
  );
}
